import { getBrand } from './brand'
import { getStage } from './stage'
import { loadScript } from './load-script'
import * as logger from '@mamdev/navigator.frontend-logger'

export const TRACKFREE_FEATURE = 'publisherCustomPurpose'
export const TRACKFREE_PURPOSE_ID = 9
const TCF_API_PATH = 'navapps/v1/js/tcf-api.js'
const TCF_API_LOAD_TIMEOUT = 3000
const TCF_API_CHECK_INTERVAL = 100
const TCF_API_CHECK_ATTEMPTS = 30

export async function loadTcfApi(): Promise<void> {
  if (isTcfApiReady()) {
    // already loaded, nothing to do
    return
  }

  const stage = getStage()
  let stagePath = 'live'
  if (stage === 'dev' || stage === 'qa') {
    stagePath = 'qa'
  }
  const tcfApiUrl = `https://dl.${getBrand()}/tcf/${stagePath}/${TCF_API_PATH}`
  logger.debug('loading TCF-API from', tcfApiUrl)

  await loadScript(tcfApiUrl, TCF_API_LOAD_TIMEOUT)
  // TCF-API initializes asynchronously so we have to wait until it's ready
  await waitForTcfApi(TCF_API_CHECK_INTERVAL, TCF_API_CHECK_ATTEMPTS)
}

export function isTcfApiReady(): boolean {
  const isReady = typeof window.__tcfapi === 'function'
  logger.debug(`TCF-API is${isReady ? '' : ' NOT'} ready`)
  return isReady
}

export async function waitForTcfApi(interval: number, maxAttempts: number): Promise<void> {
  if (isTcfApiReady()) {
    return
  }

  return new Promise((resolve, reject) => {
    let count = 0
    const handle = window.setInterval(() => {
      count = count + 1
      if (isTcfApiReady()) {
        window.clearInterval(handle)
        logger.debug(`TCF-API ready after ${count * interval}ms`)
        return resolve()
      }
      if (count > maxAttempts) {
        return reject(`TCF-API not ready after ${count * interval}ms`)
      }
    }, interval)
  })
}

export async function hasTcfPermission(featureName: string, purposeId?: number): Promise<boolean> {
  return new Promise((resolve, reject) => {
    try {
      logger.debug('checking TCF permission', featureName)
      window.__tcfapi('getPermission', 2, (value: boolean) => {
        logger.debug(`TCF permission ${featureName} value`, value)
        resolve(value)
      }, featureName, purposeId)
    } catch (e) {
      logger.error(`TCF permission check for ${featureName} failed`, e)
      reject(e)
    }
  })
}

export async function hasTrackFreeFeature(): Promise<boolean> {
  await loadTcfApi()
  return hasTcfPermission(TRACKFREE_FEATURE, TRACKFREE_PURPOSE_ID)
}
